// frontend/src/router.tsx
import { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { Layout } from "./components/Layout.js";
import { ProtectedRoute } from "./components/ProtectedRoute.js";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { CampaignsPage } from "./pages/CampaignsPage.js";
import { HypothesesPage } from "./pages/HypothesesPage.js";
import { ActionLogPage } from "./pages/ActionLogPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";

const CampaignDetailsPage = lazy(() =>
  import("./pages/CampaignDetailsPage.js").then((m) => ({ default: m.CampaignDetailsPage })),
);
const SKUListPage = lazy(() => import("./pages/SKUListPage.js").then((m) => ({ default: m.SKUListPage })));
const SKUDetailsPage = lazy(() => import("./pages/SKUDetailsPage.js").then((m) => ({ default: m.SKUDetailsPage })));
const CohortPage = lazy(() => import("./pages/CohortPage.js").then((m) => ({ default: m.CohortPage })));
const CustomerJourneyPage = lazy(() => import("./pages/CustomerJourneyPage.js").then((m) => ({ default: m.CustomerJourneyPage })));
const InsightsPage = lazy(() => import("./pages/InsightsPage.js").then((m) => ({ default: m.InsightsPage })));
const LabPage = lazy(() => import("./pages/LabPage.js").then((m) => ({ default: m.LabPage })));
const ScoreboardPage = lazy(() => import("./pages/ScoreboardPage.js").then((m) => ({ default: m.ScoreboardPage })));
const HypothesisCreatePage = lazy(() =>
  import("./pages/HypothesisCreatePage.js").then((m) => ({ default: m.HypothesisCreatePage })),
);
const HypothesisDetailsPage = lazy(() =>
  import("./pages/HypothesisDetailsPage.js").then((m) => ({ default: m.HypothesisDetailsPage })),
);
const MediaPlanPage = lazy(() => import("./pages/MediaPlanPage.js").then((m) => ({ default: m.MediaPlanPage })));
const SalesPage = lazy(() => import("./pages/SalesPage.js").then((m) => ({ default: m.SalesPage })));
const AdsSalesPage = lazy(() => import("./pages/AdsSalesPage.js").then((m) => ({ default: m.AdsSalesPage })));
const PnLPage = lazy(() => import("./pages/PnLPage.js").then((m) => ({ default: m.PnLPage })));
const BuyoutPage = lazy(() => import("./pages/BuyoutPage.js").then((m) => ({ default: m.BuyoutPage })));
const CatalogPage = lazy(() => import("./pages/CatalogPage.js").then((m) => ({ default: m.CatalogPage })));
const EmailAdminPage = lazy(() => import("./pages/EmailAdminPage.js").then((m) => ({ default: m.EmailAdminPage })));

// Marketing Control (этап 00): общий каркас управления каналами.
const MarketingTodayPage = lazy(() =>
  import("./pages/marketing/MarketingTodayPage.js").then((m) => ({ default: m.MarketingTodayPage })),
);
const MarketingExperimentsPage = lazy(() =>
  import("./pages/marketing/MarketingExperimentsPage.js").then((m) => ({ default: m.MarketingExperimentsPage })),
);
const MarketingExperimentDetailsPage = lazy(() =>
  import("./pages/marketing/MarketingExperimentDetailsPage.js").then((m) => ({
    default: m.MarketingExperimentDetailsPage,
  })),
);
const MarketingChangesPage = lazy(() =>
  import("./pages/marketing/MarketingChangesPage.js").then((m) => ({ default: m.MarketingChangesPage })),
);
const MarketingDecisionsPage = lazy(() =>
  import("./pages/marketing/MarketingDecisionsPage.js").then((m) => ({ default: m.MarketingDecisionsPage })),
);

// Доступы (T-083): экран управления секретами интеграций.
const IntegrationsPage = lazy(() =>
  import("./pages/settings/IntegrationsPage.js").then((m) => ({ default: m.IntegrationsPage })),
);

// PIM · Яндекс Маркет (T-074): состояние карточек на площадке и заявки на запись.
const MarketCatalogPage = lazy(() =>
  import("./pages/pim/MarketCatalogPage.js").then((m) => ({ default: m.MarketCatalogPage })),
);
const MarketRequestsPage = lazy(() =>
  import("./pages/pim/MarketRequestsPage.js").then((m) => ({ default: m.MarketRequestsPage })),
);
const MarketContentPage = lazy(() =>
  import("./pages/pim/MarketContentPage.js").then((m) => ({ default: m.MarketContentPage })),
);
const MarketCardPage = lazy(() =>
  import("./pages/pim/MarketCardPage.js").then((m) => ({ default: m.MarketCardPage })),
);

// PIM · витрина остатков (T-079): сколько чего и где лежит.
const StockShowcasePage = lazy(() =>
  import("./pages/pim/StockShowcasePage.js").then((m) => ({ default: m.StockShowcasePage })),
);

// Direct Control (этап 01): экраны канала «Яндекс Директ».
const DirectTodayPage = lazy(() =>
  import("./pages/direct/DirectTodayPage.js").then((m) => ({ default: m.DirectTodayPage })),
);
const DirectGoalsPage = lazy(() =>
  import("./pages/direct/DirectGoalsPage.js").then((m) => ({ default: m.DirectGoalsPage })),
);
const DirectExperimentsPage = lazy(() =>
  import("./pages/direct/DirectExperimentsPage.js").then((m) => ({ default: m.DirectExperimentsPage })),
);
const DirectExperimentDetailsPage = lazy(() =>
  import("./pages/direct/DirectExperimentDetailsPage.js").then((m) => ({
    default: m.DirectExperimentDetailsPage,
  })),
);
const DirectPlannerPage = lazy(() =>
  import("./pages/direct/DirectPlannerPage.js").then((m) => ({ default: m.DirectPlannerPage })),
);

// Direct Control (этап 03): путь владельца — гипотеза → конструкция → матрица → спецификация.
const DirectHypothesisPage = lazy(() =>
  import("./pages/direct/DirectHypothesisPage.js").then((m) => ({ default: m.DirectHypothesisPage })),
);
const DirectExperimentDesignPage = lazy(() =>
  import("./pages/direct/DirectExperimentDesignPage.js").then((m) => ({
    default: m.DirectExperimentDesignPage,
  })),
);
const DirectCampaignMatrixPage = lazy(() =>
  import("./pages/direct/DirectCampaignMatrixPage.js").then((m) => ({
    default: m.DirectCampaignMatrixPage,
  })),
);
const DirectCampaignSpecPage = lazy(() =>
  import("./pages/direct/DirectCampaignSpecPage.js").then((m) => ({
    default: m.DirectCampaignSpecPage,
  })),
);

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "campaigns", element: <CampaignsPage /> },
      {
        path: "campaigns/:id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <CampaignDetailsPage />
          </Suspense>
        ),
      },
      {
        path: "skus",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <SKUListPage />
          </Suspense>
        ),
      },
      {
        path: "skus/:product_id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <SKUDetailsPage />
          </Suspense>
        ),
      },
      {
        path: "cohorts",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <CohortPage />
          </Suspense>
        ),
      },
      {
        path: "journey",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <CustomerJourneyPage />
          </Suspense>
        ),
      },
      {
        path: "insights",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <InsightsPage />
          </Suspense>
        ),
      },
      {
        path: "lab",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <LabPage />
          </Suspense>
        ),
      },
      {
        path: "scoreboard",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <ScoreboardPage />
          </Suspense>
        ),
      },
      {
        path: "media-plan",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <MediaPlanPage />
          </Suspense>
        ),
      },
      {
        path: "sales",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <SalesPage />
          </Suspense>
        ),
      },
      {
        path: "sales-ads",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <AdsSalesPage />
          </Suspense>
        ),
      },
      {
        path: "pnl",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <PnLPage />
          </Suspense>
        ),
      },
      {
        path: "buyout",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <BuyoutPage />
          </Suspense>
        ),
      },
      {
        path: "catalog",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <CatalogPage />
          </Suspense>
        ),
      },
      {
        path: "marketing/today",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <MarketingTodayPage />
          </Suspense>
        ),
      },
      {
        path: "marketing/experiments",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <MarketingExperimentsPage />
          </Suspense>
        ),
      },
      {
        path: "marketing/experiments/:id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <MarketingExperimentDetailsPage />
          </Suspense>
        ),
      },
      {
        path: "marketing/changes",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <MarketingChangesPage />
          </Suspense>
        ),
      },
      {
        path: "marketing/decisions",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <MarketingDecisionsPage />
          </Suspense>
        ),
      },
      {
        path: "direct/today",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectTodayPage />
          </Suspense>
        ),
      },
      {
        path: "direct/goals",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectGoalsPage />
          </Suspense>
        ),
      },
      {
        path: "direct/experiments",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectExperimentsPage />
          </Suspense>
        ),
      },
      {
        path: "direct/experiments/:id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectExperimentDetailsPage />
          </Suspense>
        ),
      },
      {
        path: "direct/planner",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectPlannerPage />
          </Suspense>
        ),
      },
      {
        path: "pim/stock",
        element: (
          <Suspense fallback={<div style={{ padding: 24 }}>Загрузка…</div>}>
            <StockShowcasePage />
          </Suspense>
        ),
      },
      {
        path: "pim/market",
        element: (
          <Suspense fallback={<div style={{ padding: 24 }}>Загрузка…</div>}>
            <MarketCatalogPage />
          </Suspense>
        ),
      },
      {
        path: "pim/market/cards/:offerId",
        element: (
          <Suspense fallback={<div style={{ padding: 24 }}>Загрузка…</div>}>
            <MarketCardPage />
          </Suspense>
        ),
      },
      {
        path: "pim/market/content",
        element: (
          <Suspense fallback={<div style={{ padding: 24 }}>Загрузка…</div>}>
            <MarketContentPage />
          </Suspense>
        ),
      },
      {
        path: "pim/market/requests",
        element: (
          <Suspense fallback={<div style={{ padding: 24 }}>Загрузка…</div>}>
            <MarketRequestsPage />
          </Suspense>
        ),
      },
      {
        path: "direct/hypotheses",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectHypothesisPage />
          </Suspense>
        ),
      },
      {
        path: "direct/hypotheses/:id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectHypothesisPage />
          </Suspense>
        ),
      },
      {
        path: "direct/experiments/:id/design",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectExperimentDesignPage />
          </Suspense>
        ),
      },
      {
        path: "direct/experiments/:id/matrix",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectCampaignMatrixPage />
          </Suspense>
        ),
      },
      {
        path: "direct/blueprints/:id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <DirectCampaignSpecPage />
          </Suspense>
        ),
      },
      { path: "hypotheses", element: <HypothesesPage /> },
      {
        path: "hypotheses/new",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <HypothesisCreatePage />
          </Suspense>
        ),
      },
      {
        path: "hypotheses/:id",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <HypothesisDetailsPage />
          </Suspense>
        ),
      },
      { path: "action-log", element: <ActionLogPage /> },
      {
        path: "emails",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <EmailAdminPage />
          </Suspense>
        ),
      },
      { path: "settings", element: <SettingsPage /> },
      {
        path: "settings/integrations",
        element: (
          <Suspense fallback={<div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>}>
            <IntegrationsPage />
          </Suspense>
        ),
      },
    ],
  },
]);
